import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import EmptyState from "@/components/EmptyState";
import { formatAmount } from "@/lib/format";
import { Vote as VoteIcon, Heart, BarChart3, Check, Calendar, Gift, ShoppingBag } from "lucide-react";

const TABS = [
  { key: "all", label: "Tous" },
  { key: "votes", label: "Votes" },
  { key: "fundraisers", label: "Cagnottes" },
  { key: "gifts", label: "Cadeaux" },
  { key: "surveys", label: "Sondages" },
];

function fmtDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString("fr-FR", { day: "2-digit", month: "short", year: "numeric" });
  } catch {
    return "";
  }
}

const VOTE_TYPE_LABEL = {
  single: "Choix unique",
  multi: "Choix multiples",
  binary: "Binaire",
  approval: "Approbation",
  ranked: "Classement",
};

function VoteItem({ item }) {
  return (
    <Link to={item.link} className="z-card" style={{ padding: 16, display: "block", textDecoration: "none", color: "inherit" }} data-testid={`engagement-vote-${item.id}`}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6, flexWrap: "wrap" }}>
        <VoteIcon size={16} color="var(--z-teal)" />
        <span className="z-chip z-chip-active" style={{ fontSize: 11 }}>{VOTE_TYPE_LABEL[item.vote_type] || item.vote_type}</span>
        <span className="z-chip" style={{ fontSize: 11, background: "#e6f7f8", color: "var(--z-teal)" }}><Check size={11} /> Voté</span>
      </div>
      <h3 style={{ fontSize: 16, fontWeight: 700, margin: "4px 0", color: "var(--z-text)" }}>{item.title}</h3>
      <div style={{ fontSize: 12, color: "var(--z-muted)", marginBottom: 8 }}>
        <Calendar size={11} style={{ verticalAlign: "middle", marginRight: 4 }} />
        {fmtDate(item.participated_at)}
        {item.organizer_name && <> · {item.organizer_name}</>}
      </div>
      {item.picks?.length > 0 && (
        <div style={{ background: "var(--z-bg)", padding: 10, borderRadius: 8, fontSize: 13 }}>
          <span style={{ color: "var(--z-muted)" }}>Votre choix : </span>
          <strong style={{ color: "var(--z-teal)" }}>{item.picks.join(", ")}</strong>
        </div>
      )}
    </Link>
  );
}

function FundraiserItem({ item }) {
  const pct = item.goal > 0 ? Math.min(100, Math.round((item.raised / item.goal) * 100)) : 0;
  return (
    <Link to={item.link} className="z-card" style={{ padding: 16, display: "block", textDecoration: "none", color: "inherit" }} data-testid={`engagement-fundraiser-${item.id}`}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
        <Heart size={16} color="var(--z-orange)" />
        <span className="z-chip" style={{ fontSize: 11, background: "var(--z-orange)", color: "white" }}>Cagnotte</span>
        {item.anonymous && <span className="z-chip" style={{ fontSize: 11 }}>Anonyme</span>}
      </div>
      <h3 style={{ fontSize: 16, fontWeight: 700, margin: "4px 0", color: "var(--z-text)" }}>{item.title}</h3>
      <div style={{ fontSize: 12, color: "var(--z-muted)", marginBottom: 8 }}>
        <Calendar size={11} style={{ verticalAlign: "middle", marginRight: 4 }} />
        {fmtDate(item.participated_at)}
        {item.organizer_name && <> · {item.organizer_name}</>}
      </div>
      <div style={{ background: "var(--z-bg)", padding: 10, borderRadius: 8, fontSize: 13, marginBottom: 8 }}>
        <span style={{ color: "var(--z-muted)" }}>Vous avez donné </span>
        <strong style={{ color: "var(--z-orange)" }}>{formatAmount(item.amount, item.currency)}</strong>
      </div>
      <div className="z-progress" style={{ marginBottom: 4 }}>
        <div className="z-progress-fill" style={{ width: `${pct}%` }} />
      </div>
      <div style={{ fontSize: 12, color: "var(--z-muted)" }}>
        Levé : {formatAmount(item.raised, item.currency)} / {formatAmount(item.goal, item.currency)}
      </div>
      {item.message && (
        <div style={{ fontStyle: "italic", fontSize: 13, color: "var(--z-muted)", marginTop: 8 }}>
          « {item.message} »
        </div>
      )}
    </Link>
  );
}

function SurveyItem({ item }) {
  return (
    <Link to={item.link} className="z-card" style={{ padding: 16, display: "block", textDecoration: "none", color: "inherit" }} data-testid={`engagement-survey-${item.id}`}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
        <BarChart3 size={16} color="var(--z-teal)" />
        <span className="z-chip z-chip-active" style={{ fontSize: 11 }}>Sondage</span>
        <span className="z-chip" style={{ fontSize: 11, background: "#e6f7f8", color: "var(--z-teal)" }}><Check size={11} /> Répondu</span>
      </div>
      <h3 style={{ fontSize: 16, fontWeight: 700, margin: "4px 0", color: "var(--z-text)" }}>{item.title}</h3>
      <div style={{ fontSize: 12, color: "var(--z-muted)" }}>
        <Calendar size={11} style={{ verticalAlign: "middle", marginRight: 4 }} />
        {fmtDate(item.participated_at)}
        {item.organizer_name && <> · {item.organizer_name}</>}
        {" · "}{item.questions_count} question(s)
      </div>
    </Link>
  );
}

function GiftItem({ item }) {
  const isContribution = item.kind === "contribution";
  return (
    <Link to={item.link} className="z-card" style={{ padding: 16, display: "block", textDecoration: "none", color: "inherit" }} data-testid={`engagement-gift-${item.id}`}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6, flexWrap: "wrap" }}>
        <Gift size={16} color="var(--z-orange)" />
        <span className="z-chip" style={{ fontSize: 11, background: "var(--z-orange)", color: "white" }}>
          {isContribution ? "Contribution cadeau" : "Cadeau réservé"}
        </span>
        {item.anonymous && <span className="z-chip" style={{ fontSize: 11 }}>Anonyme</span>}
      </div>
      <div style={{ display: "flex", gap: 10, alignItems: "center", marginTop: 6 }}>
        {item.item_image_url ? (
          <img src={item.item_image_url} alt="" style={{ width: 56, height: 56, borderRadius: 8, objectFit: "cover" }} />
        ) : (
          <div style={{ width: 56, height: 56, borderRadius: 8, background: "var(--z-bg)", display: "grid", placeItems: "center" }}>
            <Gift size={22} color="var(--z-muted)" />
          </div>
        )}
        <div style={{ flex: 1, minWidth: 0 }}>
          <h3 style={{ fontSize: 15, fontWeight: 700, margin: 0, color: "var(--z-text)" }}>{item.item_name}</h3>
          <div style={{ fontSize: 12, color: "var(--z-muted)", marginTop: 2 }}>
            {item.item_category} · Liste « {item.list_title} »
          </div>
        </div>
      </div>
      <div style={{ fontSize: 12, color: "var(--z-muted)", marginTop: 8 }}>
        <Calendar size={11} style={{ verticalAlign: "middle", marginRight: 4 }} />
        {fmtDate(item.participated_at)}
        {item.organizer_name && <> · par {item.organizer_name}</>}
      </div>
      {isContribution && item.amount != null && (
        <div style={{ background: "var(--z-bg)", padding: 8, borderRadius: 8, fontSize: 13, marginTop: 8 }}>
          <span style={{ color: "var(--z-muted)" }}>Vous avez contribué </span>
          <strong style={{ color: "var(--z-orange)" }}>{formatAmount(item.amount, "EUR")}</strong>
        </div>
      )}
      {!isContribution && (
        <div style={{ background: "var(--z-bg)", padding: 8, borderRadius: 8, fontSize: 13, marginTop: 8, display: "flex", alignItems: "center", gap: 6 }}>
          <ShoppingBag size={13} color="var(--z-teal)" />
          <span style={{ color: "var(--z-muted)" }}>Ce cadeau vous est réservé.</span>
        </div>
      )}
      {item.message && (
        <div style={{ fontStyle: "italic", fontSize: 13, color: "var(--z-muted)", marginTop: 8 }}>
          « {item.message} »
        </div>
      )}
    </Link>
  );
}

export default function MyEngagementsPage() {
  const { user } = useAuth();
  const [tab, setTab] = useState("all");
  const [data, setData] = useState({ votes: [], fundraisers: [], surveys: [], gifts: [], total: 0 });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) return;
    api.get("/me/engagements")
      .then((r) => setData(r.data))
      .catch(() => setData({ votes: [], fundraisers: [], surveys: [], gifts: [], total: 0 }))
      .finally(() => setLoading(false));
  }, [user]);

  if (!user) {
    return (
      <div className="z-container" data-testid="engagements-page-guest">
        <h1 className="z-page-title">Mes engagements</h1>
        <p className="z-page-sub">Connectez-vous pour voir vos participations.</p>
        <Link to="/login" className="z-btn z-btn-primary" data-testid="engagements-login-btn">Se connecter</Link>
      </div>
    );
  }

  if (loading) {
    return <div className="z-container" style={{ textAlign: "center" }}><span className="z-loader z-loader-dark" /></div>;
  }

  const renderAll = () => {
    const items = [
      ...data.votes.map((x) => ({ ...x, _k: `v-${x.id}-${x.participated_at}`, _ts: x.participated_at })),
      ...data.fundraisers.map((x) => ({ ...x, _k: `f-${x.id}-${x.participated_at}`, _ts: x.participated_at })),
      ...data.surveys.map((x) => ({ ...x, _k: `s-${x.id}-${x.participated_at}`, _ts: x.participated_at })),
      ...((data.gifts || []).map((x) => ({ ...x, _k: `g-${x.id}-${x.participated_at}`, _ts: x.participated_at }))),
    ].sort((a, b) => (b._ts || "").localeCompare(a._ts || ""));
    return items;
  };

  const renderItem = (item) => {
    if (item.type === "vote") return <VoteItem key={item._k || item.id} item={item} />;
    if (item.type === "fundraiser") return <FundraiserItem key={item._k || item.id} item={item} />;
    if (item.type === "survey") return <SurveyItem key={item._k || item.id} item={item} />;
    if (item.type === "gift") return <GiftItem key={item._k || item.id} item={item} />;
    return null;
  };

  let items = [];
  if (tab === "all") items = renderAll();
  else if (tab === "votes") items = data.votes;
  else if (tab === "fundraisers") items = data.fundraisers;
  else if (tab === "surveys") items = data.surveys;
  else if (tab === "gifts") items = data.gifts || [];

  return (
    <div className="z-container" data-testid="engagements-page">
      <h1 className="z-page-title">Mes engagements</h1>
      <p className="z-page-sub">Retrouvez tous les votes, cagnottes, sondages et cadeaux auxquels vous avez participé.</p>

      <div style={{ display: "flex", gap: 8, marginBottom: 18, flexWrap: "wrap" }}>
        {TABS.map((t) => {
          const count = t.key === "all" ? data.total : (data[t.key]?.length || 0);
          return (
            <button
              key={t.key}
              type="button"
              onClick={() => setTab(t.key)}
              className={`z-chip ${tab === t.key ? "z-chip-active" : ""}`}
              data-testid={`engagements-tab-${t.key}`}
            >
              {t.label} ({count})
            </button>
          );
        })}
      </div>

      {items.length === 0 ? (
        <EmptyState
          icon={<Heart size={28} />}
          title="Aucune participation pour l'instant"
          message="Explorez les votes et cagnottes pour faire entendre votre voix."
          testId="engagements-empty"
        >
          <Link to="/participer" className="z-btn z-btn-primary" data-testid="engagements-discover-btn">
            Découvrir
          </Link>
        </EmptyState>
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(300px,1fr))", gap: 14 }}>
          {items.map(renderItem)}
        </div>
      )}
    </div>
  );
}
